import { useTranslator } from "@eo-locale/react";

import { ChangeEvent, useEffect } from "react";

import { CommonFileQuestion } from "@/storybook-components";
import { questionNumber, questionTitleWithNumber } from "@/helpers/survey";
import { fileUpload } from "@/models";
import useUser from "@/hooks/useUser";

import {
  setSurveyAnswers,
  setSurveyErrors,
  removeSurveyAnswers
} from "@/redux/features/survey/slice";
import type { Question } from "@/types";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import useEmbeds from "@/hooks/useEmbeds";
import googleTranslateInit from "@/helpers/google-translate-init";
import { fetchAuthToken } from "@/helpers/fetch-auth-token";
import { spacing, textStyles } from "./question-styles";

interface IFileQuestion {
  question: Question;
}

const MAX_FILE_SIZE = 200000000;

const FileQuestion = ({ question }: IFileQuestion): JSX.Element => {
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();
  const { isEmbed } = useEmbeds();
  const user = useAppSelector((state) => state.user);
  const anonymousUser = useAppSelector((state) => state.anonymousUser);
  const { token } = fetchAuthToken({ user, anonymousUser });

  const survey = useAppSelector((state) => state.survey);
  const { answers, errors, saving } = survey;
  const { baseUrl } = useAppSelector((state) => state.adapter.adapter);
  const { settings } = useAppSelector((state) => state.settings);
  const { googleTranslationEnabled } = settings;
  const { isPreviewUser } = useUser();

  const error = errors[question.id] ?? {};
  const answer = answers[question.id] ?? {};

  useEffect(() => {
    if (isPreviewUser) {
      dispatch(
        setSurveyAnswers({
          questionId: question.id,
          answer: {
            value: "placeholder", // cannot be empty
            file_name: "example-document.pdf",
            file_size: 0,
            file_content_type: "application/pdf"
          }
        })
      );
    }
  }, []);

  const handleChange = async (
    e: ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    const selectedFile = e.target.files[0];
    // initiate google translate when removing the image to translate loading text
    googleTranslateInit(isEmbed, googleTranslationEnabled);
    if (!selectedFile) return;

    if (selectedFile.size > MAX_FILE_SIZE) {
      dispatch(
        setSurveyErrors({
          questionId: question.id,
          error: { code: "tooBigFile" }
        })
      );
      return;
    }

    try {
      const file = await fileUpload(token, baseUrl, selectedFile);
      dispatch(
        setSurveyAnswers({
          questionId: question.id,
          answer: {
            value: file.url,
            file_name: selectedFile.name,
            file_size: selectedFile.size,
            file_content_type: selectedFile.type
          }
        })
      );
      // initiate google translate when adding a file to translate the delete, replace buttons
      googleTranslateInit(isEmbed, googleTranslationEnabled);
      // eslint-disable-next-line no-unused-vars
    } catch (error) {
      dispatch(
        setSurveyErrors({
          questionId: question.id,
          error: { code: "uploadError" }
        })
      );
    }
  };

  const removeImage = () => {
    // initiate google translate when removing the image to translate placeholders
    googleTranslateInit(isEmbed, googleTranslationEnabled);
    dispatch(
      removeSurveyAnswers({
        questionId: question.id
      })
    );
  };

  return (
    <CommonFileQuestion
      layoutProps={{ width: "100%" }}
      isInvalid={!!error.code}
      isRequired={question.required}
      className={error.code ? "ehq-question-error" : null}
      spacing={spacing.question}
      questionLabel={questionTitleWithNumber(
        questionNumber(question, survey),
        question
      )}
      questionLabelProps={{ ...textStyles?.label }}
      labelClassName="ehq-surveyQuestionHeading"
      accessibilityTextOfRequired={`* ${translate("common.required")}`}
      answer={answer}
      answerAriaLabel={
        answer?.file_name
          ? translate("common.changeFile")
          : translate("common.chooseFile")
      }
      questionId={question?.id}
      isDisabled={saving}
      handleFileUpload={handleChange}
      loadingText={`${translate("common.uploading")}...`}
      deleteText={translate("common.delete")}
      replaceText={translate("common.replace")}
      onDelete={removeImage}
      isDeleteDisabled={isPreviewUser}
      isReplaceDisabled={isPreviewUser}
      contentClassName="ehq-surveyFileInputFeild"
      size={{ base: "lg", lg: "md" }}
      answerProps={{ ...textStyles?.content }}
      footerStackSpacing={spacing.footerStack}
      note={question.notes}
      noteProps={{ ...textStyles?.footer }}
      errorMessage={error?.code && translate(`errors.${error.code}`)}
      errorMessageProps={{ ...textStyles?.error }}
      uploadButtonText={translate("questions.fileUpload.uploadFile")}
    />
  );
};

export default FileQuestion;
